/**
 * LUFS (Loudness Units relative to Full Scale) calculation
 * Simplified implementation based on ITU-R BS.1770
 */

/**
 * K-weighting filter coefficients for 48kHz
 * Pre-filter (high shelf) and RLB weighting
 */
interface FilterCoefficients {
  b: number[]
  a: number[]
}

// Pre-filter (high shelf) coefficients for 48kHz
const PRE_FILTER_48K: FilterCoefficients = {
  b: [1.53512485958697, -2.69169618940638, 1.19839281085285],
  a: [1.0, -1.69065929318241, 0.73248077421585],
}

// RLB (Revised Low-frequency B-weighting) coefficients for 48kHz
const RLB_FILTER_48K: FilterCoefficients = {
  b: [1.0, -2.0, 1.0],
  a: [1.0, -1.99004745483398, 0.99007225036621],
}

/**
 * Apply a biquad IIR filter
 */
function applyFilter(
  samples: Float32Array,
  coeffs: FilterCoefficients
): Float32Array {
  const output = new Float32Array(samples.length)
  const { b, a } = coeffs

  // IIR filter difference equation
  for (let i = 0; i < samples.length; i++) {
    output[i] = b[0] * samples[i]

    if (i >= 1) {
      output[i] += b[1] * samples[i - 1] - a[1] * output[i - 1]
    }
    if (i >= 2) {
      output[i] += b[2] * samples[i - 2] - a[2] * output[i - 2]
    }
  }

  return output
}

/**
 * Apply K-weighting filter to samples
 */
function applyKWeighting(samples: Float32Array): Float32Array {
  // First apply pre-filter (high shelf)
  const preFiltered = applyFilter(samples, PRE_FILTER_48K)
  // Then apply RLB weighting
  return applyFilter(preFiltered, RLB_FILTER_48K)
}

/**
 * Calculate mean square of samples in a block
 */
function calculateBlockMeanSquare(samples: Float32Array): number {
  let sum = 0
  for (let i = 0; i < samples.length; i++) {
    sum += samples[i] * samples[i]
  }
  return sum / samples.length
}

/**
 * Calculate integrated LUFS for an audio buffer
 * Simplified version of ITU-R BS.1770
 */
export function calculateLUFS(
  audioBuffer: AudioBuffer,
  blockDurationMs: number = 400,
  overlapMs: number = 300
): number {
  const sampleRate = audioBuffer.sampleRate
  const blockSize = Math.floor((blockDurationMs / 1000) * sampleRate)
  const hopSize = Math.floor(((blockDurationMs - overlapMs) / 1000) * sampleRate)

  // Get mono signal (or average channels)
  let mono: Float32Array
  if (audioBuffer.numberOfChannels === 1) {
    mono = audioBuffer.getChannelData(0)
  } else {
    const left = audioBuffer.getChannelData(0)
    const right = audioBuffer.getChannelData(1)
    mono = new Float32Array(left.length)
    for (let i = 0; i < left.length; i++) {
      mono[i] = (left[i] + right[i]) / 2
    }
  }

  // Apply K-weighting
  const weighted = applyKWeighting(mono)

  // Calculate mean square for each block
  const blockMeanSquares: number[] = []
  for (let i = 0; i + blockSize <= weighted.length; i += hopSize) {
    const block = weighted.slice(i, i + blockSize)
    blockMeanSquares.push(calculateBlockMeanSquare(block))
  }

  if (blockMeanSquares.length === 0) {
    return -70.0 // Silence
  }

  // Calculate absolute threshold (-70 LUFS)
  const absoluteThreshold = Math.pow(10, -7.0) // -70 LUFS as linear power

  // First pass: find blocks above absolute threshold
  const blocksAboveAbsolute = blockMeanSquares.filter((ms) => ms > absoluteThreshold)

  if (blocksAboveAbsolute.length === 0) {
    return -70.0 // Below threshold
  }

  // Calculate mean of blocks above absolute threshold
  const avgAboveAbsolute =
    blocksAboveAbsolute.reduce((a, b) => a + b, 0) / blocksAboveAbsolute.length

  // Relative threshold: -10 dB below first pass average
  const relativeThreshold = avgAboveAbsolute * Math.pow(10, -1.0)

  // Second pass: blocks above relative threshold
  const blocksAboveRelative = blockMeanSquares.filter((ms) => ms > relativeThreshold)

  if (blocksAboveRelative.length === 0) {
    return -70.0
  }

  // Final LUFS calculation
  const avgAboveRelative =
    blocksAboveRelative.reduce((a, b) => a + b, 0) / blocksAboveRelative.length

  // Convert to LUFS (with -0.691 offset per ITU-R BS.1770)
  const lufs = -0.691 + 10 * Math.log10(avgAboveRelative)

  // Clamp to reasonable range
  return Math.max(-70.0, Math.min(0, lufs))
}

/**
 * Get loudness target for different streaming platforms
 */
export interface StreamingTarget {
  name: string
  lufs: number
  description: string
}

export const STREAMING_TARGETS: StreamingTarget[] = [
  { name: 'Spotify', lufs: -14, description: 'Spotify normalizes to -14 LUFS' },
  { name: 'Apple Music', lufs: -16, description: 'Apple Music normalizes to -16 LUFS' },
  { name: 'YouTube', lufs: -14, description: 'YouTube normalizes to -14 LUFS' },
  { name: 'SoundCloud', lufs: -14, description: 'SoundCloud normalizes to around -14 LUFS' },
  { name: 'Tidal', lufs: -14, description: 'Tidal normalizes to -14 LUFS' },
]

/**
 * Check if current loudness is appropriate for streaming
 */
export function checkLoudnessForStreaming(
  currentLufs: number
): { status: 'good' | 'too_loud' | 'too_quiet'; message: string } {
  if (currentLufs > -10) {
    return {
      status: 'too_loud',
      message: `At ${currentLufs.toFixed(1)} LUFS, your track is too loud and will be turned down by streaming platforms`,
    }
  } else if (currentLufs < -20) {
    return {
      status: 'too_quiet',
      message: `At ${currentLufs.toFixed(1)} LUFS, your track is quieter than typical streaming content`,
    }
  }
  return {
    status: 'good',
    message: `${currentLufs.toFixed(1)} LUFS is within the typical range for streaming platforms`,
  }
}
